/**
 * The module catalogue — one entry per card.
 *
 * Single source for the left navigation AND the module grid on the home page:
 * the two rendered from separate lists is how a module ends up in the sidebar
 * but nowhere else (or reachable from a tile that navigates to a stale path).
 *
 * `id` is the card name and matches the backend 1-to-1, so access filtering is
 * `hasCardAccess(permissions, module.id)` with nothing to translate. `path` is
 * the route to open — it lives here rather than in a chain of `if (id === ...)`
 * branches so adding a module is one entry in one file.
 *
 * A new card still has to be registered in the four places CLAUDE.md lists
 * (Role.js enum, roleController catalogue, PermissionEditor, and here).
 */

import {
    HiOutlineChartBar,
    HiOutlineUserGroup,
    HiOutlineClipboardCheck,
    HiOutlineDocumentText,
    HiOutlineCreditCard,
    HiOutlineCash,
    HiOutlineBookOpen,
    HiOutlineClipboardList,
    HiOutlineIdentification,
    HiOutlineScale,
    HiOutlineReceiptTax,
    HiOutlineClock,
    HiOutlineDeviceMobile,
    HiOutlineCube,
    HiOutlineTruck,
    HiOutlineUserCircle,
    HiOutlineEye,
    HiOutlineTrendingUp,
    HiOutlineVideoCamera,
    HiOutlineRefresh,
    HiOutlineFolderOpen,
    HiOutlineFlag,
    HiOutlinePresentationChartLine,
    HiOutlineShieldCheck
} from 'react-icons/hi';

export const MODULES = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        shortTitle: 'Dashboard',
        description: 'View analytics and overview',
        color: '#9333ea',
        path: '/manager/dashboard',
        icon: HiOutlineChartBar
    },
    {
        id: 'visitor-entries',
        title: 'Visitor Entries',
        shortTitle: 'Visitor Entries',
        description: 'View and verify visitor form submissions',
        color: '#3b82f6',
        path: '/visitor-entries',
        icon: HiOutlineUserGroup
    },
    {
        id: 'task-management',
        title: 'Task Management',
        shortTitle: 'Task Management',
        description: 'Create, assign and track tasks',
        color: '#10b981',
        path: '/tasks',
        icon: HiOutlineClipboardCheck
    },
    {
        id: 'monthly-documents',
        title: 'Daily Documents',
        shortTitle: 'Daily Documents',
        description: 'Document acknowledgement & verification',
        color: '#ec4899',
        path: '/monthly-documents',
        icon: HiOutlineDocumentText
    },
    {
        id: 'cheque-register',
        title: 'Cheque Issue Register',
        shortTitle: 'Cheque Issue',
        description: 'Record cheque payments with digital signature',
        color: '#14b8a6',
        path: '/cheque-register',
        icon: HiOutlineCreditCard
    },
    {
        id: 'cashbook',
        title: 'Cashbook',
        shortTitle: 'Cashbook',
        description: 'Record cash payments with digital signature',
        color: '#06b6d4',
        path: '/cashbook',
        icon: HiOutlineCash
    },
    {
        id: 'document-log-book',
        title: 'Document Log Book',
        shortTitle: 'Document Log Book',
        description: 'Record document handover with digital acknowledgment',
        color: '#8b5cf6',
        path: '/document-log-book',
        icon: HiOutlineBookOpen
    },
    {
        id: 'clearance-form',
        title: 'Clearance Form',
        shortTitle: 'Clearance Form',
        description: 'Employee exit/transfer clearance with section sign-off',
        color: '#0891b2',
        path: '/clearance-form',
        icon: HiOutlineIdentification
    },
    {
        id: 'register-book',
        title: 'Register Book',
        shortTitle: 'Register Book',
        description: 'Visitor and personnel entry/exit tracking',
        color: '#ef4444',
        path: '/register-book',
        icon: HiOutlineClipboardList
    },
    {
        id: 'sales-target-management',
        title: 'Sales Target Management',
        shortTitle: 'Sales Target',
        description: 'Manage sales targets, reports and leaderboards',
        color: '#f59e0b',
        path: '/sales-target',
        icon: HiOutlineFlag
    },
    {
        id: 'gp-report',
        title: 'Sales Analysis',
        shortTitle: 'Sales Analysis',
        description: 'Sales & Gross Profit analysis — ERP imports, dashboards and month-wise reports',
        color: '#065f46',
        path: '/gp-report',
        icon: HiOutlinePresentationChartLine
    },
    {
        id: 'legal-cases',
        title: 'Legal Cases',
        shortTitle: 'Legal Cases',
        description: 'Legal cases, recoveries, settlements and approvals',
        color: '#1d4ed8',
        path: '/legal-cases',
        icon: HiOutlineScale
    },
    {
        id: 'invoice-management',
        title: 'Invoice Management',
        shortTitle: 'Invoice Management',
        description: 'View, manage and verify invoices',
        color: '#7c3aed',
        path: '/invoice-management',
        icon: HiOutlineReceiptTax
    },
    {
        id: 'aging-reports',
        title: 'Aging Reports',
        shortTitle: 'Aging Reports',
        description: 'Manage aging reports and customer emails',
        color: '#f97316',
        path: '/aging-reports',
        icon: HiOutlineClock
    },
    {
        id: 'payslip-distribution',
        title: 'Payslip Distribution',
        shortTitle: 'Payslip Distribution',
        description: 'Split payroll PDFs and send each payslip over WhatsApp',
        color: '#0ea5e9',
        path: '/payslip-distribution',
        icon: HiOutlineDeviceMobile
    },
    {
        id: 'stock-reorder',
        title: 'Stock Reorder',
        shortTitle: 'Stock Reorder',
        description: 'Generate reorder sheets from stock movement data',
        color: '#ea580c',
        path: '/stock-reorder',
        icon: HiOutlineCube
    },
    {
        id: 'delivery-management',
        title: 'Delivery Management',
        shortTitle: 'Delivery',
        description: 'Track and manage deliveries',
        color: '#10b981',
        path: '/delivery-management',
        icon: HiOutlineTruck
    },
    {
        id: 'customer-register',
        title: 'Customer Register',
        shortTitle: 'Customer Register',
        description: 'Store customer name, mobile, nationality and address',
        color: '#0ea5e9',
        path: '/customer-register',
        icon: HiOutlineUserCircle
    },
    {
        id: 'vision',
        title: 'Strategic Vision',
        shortTitle: 'Strategic Vision',
        description: 'Multi-year mission tracker — set goals, assign teams & track progress',
        color: '#711b45',
        path: '/vision',
        icon: HiOutlineEye
    },
    {
        id: 'financial-performance',
        title: 'Financial Performance',
        shortTitle: 'Financial Performance',
        description: 'P&L analytics — compare months, years and drill into profit movement',
        color: '#0f766e',
        path: '/financial-performance',
        icon: HiOutlineTrendingUp
    },
    {
        id: 'weekly-meetings',
        title: 'Weekly Meetings',
        shortTitle: 'Weekly Meetings',
        description: 'Upload meeting videos & presentations and track your points',
        color: '#6d28d9',
        path: '/weekly-meetings',
        icon: HiOutlineVideoCamera
    },
    {
        id: 'subscription-payments',
        title: 'Payments',
        shortTitle: 'Payments',
        description: 'Recurring subscriptions & variable spend — record payments, get reminders',
        color: '#1e40af',
        path: '/subscription-payments',
        icon: HiOutlineRefresh
    },
    {
        id: 'file-manager',
        title: 'My Files',
        shortTitle: 'My Files',
        description: 'Your own private folder — upload, organise and download your files',
        color: '#b45309',
        path: '/file-manager',
        icon: HiOutlineFolderOpen
    },
    {
        id: 'action-log',
        title: 'Action Log',
        shortTitle: 'Action Log',
        description: 'Tamper-evident record of every operation in PrimeOffice',
        color: '#334155',
        path: '/action-log',
        icon: HiOutlineShieldCheck
    },
    {
        id: 'working-time',
        title: 'Working Time',
        shortTitle: 'Working Time',
        description: 'Active and idle hours from the Windows agent — daily and monthly reports',
        color: '#0f766e',
        path: '/working-time',
        icon: HiOutlineClock
    },
    {
        id: 'frozen-pick-portal',
        title: 'Frozen Picking',
        shortTitle: 'Frozen Picking',
        description: 'Detects frozen items on posted ERP invoices and routes them to the freezer team’s pick board',
        color: '#0369a1',
        path: '/frozen-pick-portal',
        icon: HiOutlineCube
    }
];
